import * as React from 'react';
import { useNavigate } from 'react-router';
import type { PiletApi } from '@chess/platform-web';
import { CHESS_API, type Chess } from '@chess/api-chess';
import { reviewPath } from '@chess/api-games';
import { STORE_API, type GameStore, type Phase } from '@chess/api-model';
import { useSelectedGame } from '@chess/api-navigation/react';
import { registerCard } from '@chess/api-dashboard';
import { follow, useLatest } from './store';
import { GameTitleBar, LatestGameCard as Card } from './LatestGameCard.tsx';
import '@chess/api-insights/charts.css';
import './latest-game.css';

// The selected game on Home (`?game=`, the most recent without one): the
// containers of its title under the game strip and of its card. Both read
// this plugin's store, which follows the game store; absent altogether while
// the last 30 days hold no games.

export function setup(app: PiletApi) {
  const getApi = <T,>(name: string) => app.getApi<T>(name);
  /** Follows the selected game in this plugin's store while mounted. */
  const useFollow = () => {
    const selected = useSelectedGame(getApi);
    const id = selected.game?.id;
    React.useEffect(() => {
      let stop = () => {};
      let cancelled = false;
      Promise.all([app.getApi<GameStore>(STORE_API), app.getApi<Chess>(CHESS_API)]).then(([store, chess]) => {
        if (cancelled) return;
        stop = follow({ store, chess, id });
      });
      return () => {
        cancelled = true;
        stop();
      };
    }, [id]);
  };

  const LatestGameCard: React.FC = () => {
    const navigate = useNavigate();
    const status = useLatest((s) => s.status);
    const latest = useLatest((s) => s.latest);
    useFollow();

    // Still loading: the card's skeleton, never an empty space. No games in
    // the last 30 days, or unreadable: no card at all.
    if (status === 'loading') return <Card loading />;
    if (status !== 'ready' || !latest) return null;

    const path = reviewPath(latest.game);
    // A stage's tile opens the review at the stage's first position, as the per-game sections' link.
    const stageHref = (phase: Phase) => {
      const ply = latest.metrics?.stageStart[phase];
      return ply === undefined ? undefined : `${path}#ply=${ply}`;
    };
    return (
      <Card
        {...latest}
        href={path}
        onOpen={() => navigate(path)}
        stageHref={stageHref}
        onOpenStage={(phase) => {
          const href = stageHref(phase);
          if (href) navigate(href);
        }}
      />
    );
  };

  const Title: React.FC = () => {
    const status = useLatest((s) => s.status);
    const latest = useLatest((s) => s.latest);
    useFollow();
    if (status === 'loading') return <GameTitleBar loading />;
    if (status !== 'ready' || !latest) return null;
    return <GameTitleBar game={latest.game} headers={latest.headers} />;
  };
  // The title under the game strip (2), above the card (4) and everything else about the game.
  app.registerExtension('game.sections', Title, { order: 3 });

  // The card heads Home, under the game's title (plugin `screen-game`'s
  // `dashboard.section` on `game.card`). It draws its own card, so the
  // section renders it as it is, without the dashboard's frame.
  registerCard(app, 'game.card', LatestGameCard, { order: 10, title: 'Latest game', frame: 'none' });
}
